import { type Dayjs } from "dayjs";

import { type DateRange } from "~/modules/Dashboard/AppStats/hooks/types";

export type ComparisonMode = "previousMonth" | "previousPeriod";

export type ResolvedRange = [Dayjs, Dayjs];

export const DEFAULT_COMPARISON_MODE: ComparisonMode = "previousMonth";

export const resolveRange = (range: DateRange): ResolvedRange | null => {
  const start = range?.[0];
  const end = range?.[1];

  if (!start || !end) {
    return null;
  }

  return [start.startOf("day"), end.endOf("day")];
};

/**
 * The window a period is compared against.
 *
 * `previousMonth` shifts both ends back by one calendar month, so "1–4.09" is compared with "1–4.08".
 * `previousPeriod` is the window of the same length directly before the selected one, so "1–4.09"
 * is compared with "28–31.08". Month-end sales pushes make the latter look like a collapse at the
 * start of the next month, which is why `previousMonth` is the default.
 */
export const getComparisonRange = (range: DateRange, mode: ComparisonMode): ResolvedRange | null => {
  const resolved = resolveRange(range);

  if (!resolved) {
    return null;
  }

  const [start, end] = resolved;

  if (mode === "previousMonth") {
    return [start.subtract(1, "month").startOf("day"), end.subtract(1, "month").endOf("day")];
  }

  const lengthInDays = end.diff(start, "days") + 1;

  return [start.subtract(lengthInDays, "days").startOf("day"), end.subtract(lengthInDays, "days").endOf("day")];
};

/** Short human label for a range: "1–4.08", "28.08–4.09" or "28.12.2025–4.01.2026". */
export const formatRangeLabel = ([start, end]: ResolvedRange): string => {
  if (start.isSame(end, "day")) {
    return start.format("D.MM");
  }

  if (!start.isSame(end, "year")) {
    return `${start.format("D.MM.YYYY")}–${end.format("D.MM.YYYY")}`;
  }

  if (!start.isSame(end, "month")) {
    return `${start.format("D.MM")}–${end.format("D.MM")}`;
  }

  return `${start.format("D")}–${end.format("D.MM")}`;
};
